
import { defineComponent, ref, computed, onMounted, inject } from 'vue';
import { Modal } from 'bootstrap';
import { Apollo, Notify } from '@/core/services';
import TableContentLoader from '@/components/Table/TableContentLoader.vue';
import InnerLoader from '@/components/InnerLoader.vue';
import { GET_PROMOTIONS, GET_PROMOTION_RECIPIENTS } from '@/modules/system/promotions/graphql/Queries';
import { DELETE_PROMOTION } from '@/modules/system/promotions/graphql/Mutations';
import Table from '@/components/Table/Table.vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

export default defineComponent({
    name: 'promotion-list',
    components: { Table, TableContentLoader, InnerLoader },
    props: {
        promotionType: {
            type: String,
            default: 'PRODUCT'
        }
    },
    setup(props) {
        const loader = ref(false);
        const promotions = ref([]);
        const emitter: any = inject('emitter');
        const activeIndex = ref(-1);
        const columns = ref([
            ...(props.promotionType === 'SERVICE'
                ? [
                      { label: 'message.TITLE', key: 'title' },
                      { label: 'message.PROMOTION_MESSAGE', key: 'message' },
                      { label: 'message.URL', key: 'url' }
                  ]
                : [
                      { label: 'message.IMAGE', key: 'image' },
                      { label: 'message.URL', key: 'url' }
                  ]),
            {
                label: 'message.AUDIENCE',
                key: 'audience_type'
            },
            {
                label: 'message.RECIPIENTS',
                key: 'audience_summary'
            },
            ...(props.promotionType === 'SERVICE'
                ? [
                      // {
                      //     label: 'message.START_DATE',
                      //     key: 'starts_at'
                      // },
                      // {
                      //     label: 'message.END_DATE',
                      //     key: 'ends_at'
                      // },
                  ]
                : [
                      {
                          label: 'message.START_DATE',
                          key: 'starts_at'
                      },
                      {
                          label: 'message.END_DATE',
                          key: 'ends_at'
                      }
                  ]),
            {
                label: 'message.STATUS',
                key: 'status'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);
        const store = useStore();
        const i18n = useI18n();
        const recipientsModal: any = ref();
        const recipientsModalEl = ref<HTMLElement | null>(null);
        const selectedPromotion: any = ref(null);
        const recipients = ref<Record<string, any>[]>([]);
        const recipientsLoading = ref(false);
        const recipientSearch = ref('');

        const extraCount = (item: Record<any, any>) => {
            const shown = item?.recipient_preview?.length || 0;
            return Math.max(0, (item?.recipient_count || 0) - shown);
        };

        const filteredRecipients = computed(() => {
            const term = recipientSearch.value.trim().toLowerCase();
            if (!term) {
                return recipients.value;
            }
            return recipients.value.filter(customer => {
                return [customer.name, customer.email, customer.phone_number, customer.economic_number, customer.customer_group]
                    .join(' ')
                    .toLowerCase()
                    .includes(term);
            });
        });

        const openRecipients = async (item: Record<any, any>) => {
            selectedPromotion.value = item;
            recipientSearch.value = '';
            recipients.value = [];
            recipientsLoading.value = true;
            const modalEl = recipientsModalEl.value as HTMLElement;
            recipientsModal.value = Modal.getInstance(modalEl) || new Modal(modalEl);
            recipientsModal.value.show();
            try {
                const result = await Apollo.query({
                    query: GET_PROMOTION_RECIPIENTS,
                    variables: { id: item.id },
                    fetchPolicy: 'network-only'
                });
                recipients.value = result.data?.promotion_recipients || [];
            } catch (error) {
                recipients.value = [];
            } finally {
                recipientsLoading.value = false;
            }
        };

        const formatDate = (value: string) => {
            if (!value) {
                return '-';
            }
            const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
            if (Number.isNaN(date.getTime())) {
                return '-';
            }
            const locales = { en: 'en-GB', da: 'da', se: 'sv' };
            const locale = locales[i18n.locale.value] || 'en-GB';
            return new Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }).format(date);
        };

        const audienceLabel = (type: string) => {
            const labels = {
                ALL: 'message.ALL_CUSTOMERS',
                CUSTOMERS: 'message.SPECIFIC_CUSTOMERS',
                CUSTOMER_GROUPS: 'message.CUSTOMER_GROUPS'
            };
            return i18n.t(labels[type] || type);
        };

        const dropdownHandler = (index, id) => {
            activeIndex.value = index === id ? null : id;
        };
        const handleClickOutside = event => {
            if (!event.target.closest('.action-btn')) {
                activeIndex.value = -1;
            }
        };

        const promotionQuery = () => ({
            query: GET_PROMOTIONS,
            variables: { type: props.promotionType }
        });

        const getPromotions = () => {
            loader.value = true;
            Apollo.watchQuery({
                ...promotionQuery(),
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only',
                errorPolicy: 'all'
            }).subscribe(({ data, error }) => {
                loader.value = false;
                if (error) {
                    return;
                }
                promotions.value = data?.promotions || [];
            });
        };

        const deletePromotion = (id: number) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_PROMOTION,
                        variables: { id: id },
                        update: (cache, { data: { delete_promotion } }) => {
                            const readData = cache.readQuery(promotionQuery()) as Record<any, any>;
                            const data = readData.promotions.filter((item: any) => item.id !== delete_promotion);
                            cache.writeQuery({
                                ...promotionQuery(),
                                data: {
                                    promotions: [...data]
                                }
                            });
                            promotions.value = data;
                            loader.value = false;
                        }
                    });
                    Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
                })
                .catch(() => {
                    loader.value = false;
                });
        };

        emitter.on('reloadPromotions', () => {
            getPromotions();
        });

        onMounted(() => {
            getPromotions();
            document.addEventListener('click', handleClickOutside);
        });

        return {
            loader,
            promotions,
            columns,
            activeIndex,
            dropdownHandler,
            deletePromotion,
            audienceLabel,
            formatDate,
            extraCount,
            openRecipients,
            recipientsModalEl,
            selectedPromotion,
            recipientsLoading,
            recipientSearch,
            filteredRecipients
        };
    }
});
